/**
 * ObstructionStatusField
 *
 * Inline-editable status control for the obstruction detail panel. Wraps the
 * shared `InlineStatusMenu`, offering every status other than the current one
 * (the backend permits any cross-status transition). In `readOnly` mode (public
 * share) it renders a plain colored label. Permission enforcement is
 * server-side; the frontend only hides the affordance.
 */
import { memo, useMemo, type CSSProperties } from 'react';
import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useGeoMarkupStore } from '@/store';
import {
  InlineStatusMenu,
  type InlineStatusOption,
} from '@/components/common/InlineStatusMenu';
import { OBSTRUCTION_STATUS_COLORS } from '@/constants/obstructions';
import { performObstructionStatusChange } from '@/utils/obstructionStatusChangeHelper';
import { ObstructionStatus } from '@/types/obstructions';

import styles from '../ObstructionGeneralInfo.module.css';

interface ObstructionStatusFieldProps {
  obstructionId: string;
  status: ObstructionStatus;
  readOnly: boolean;
}

export const ObstructionStatusField = memo(function ObstructionStatusField({
  obstructionId,
  status,
  readOnly,
}: ObstructionStatusFieldProps) {
  const { t } = useTranslation();
  const updateStatus = useGeoMarkupStore(
    (state) => state.obstructions.updateStatus
  );

  const statusStyle = {
    '--obstruction-status-color': OBSTRUCTION_STATUS_COLORS[status],
  } as CSSProperties;

  const triggerLabel = (
    <Text size="sm" className={styles.statusValue} style={statusStyle}>
      {t(`obstructions.status.${status}`)}
    </Text>
  );

  const options: InlineStatusOption<ObstructionStatus>[] = useMemo(
    () =>
      Object.values(ObstructionStatus)
        .filter((target) => target !== status)
        .map((target) => ({
          value: target,
          label: t(`obstructions.status.${target}`),
        })),
    [status, t]
  );

  return (
    <InlineStatusMenu
      triggerLabel={triggerLabel}
      options={options}
      onSelect={(nextStatus) => {
        void performObstructionStatusChange(
          obstructionId,
          nextStatus,
          updateStatus
        );
      }}
      readOnly={readOnly}
    />
  );
});
